import { z } from "zod";

export const inspectorSearchSchema = z.object({
  runId: z.string().min(1).max(512).optional(),
  attemptId: z.string().min(1).max(512).optional(),
  view: z.enum(["attempt", "styles"]).optional(),
  t: z.number().finite().nonnegative().optional(),
});
export type InspectorSearch = z.infer<typeof inspectorSearchSchema>;

const jsonRecord = z.record(z.unknown());

export const inspectionMediaSchema = z.object({
  assetId: z.string(),
  role: z.enum(["footage", "supplied", "generated", "output"]),
  kind: z.string().nullable(),
  url: z.string().nullable(),
  description: z.string().nullable(),
  prompt: z.string().nullable(),
  shotIndexes: z.array(z.number().int()),
});

export const inspectionShotSchema = z.object({
  index: z.number().int(),
  shotStyleId: z.string(),
  name: z.string().nullable(),
  category: z.string().nullable(),
  summary: z.string().nullable(),
  startMs: z.number().int(),
  durationMs: z.number().int(),
  parameters: jsonRecord,
  mediaAssetIds: z.array(z.string()),
});

export const inspectionToolCallSchema = z.object({
  name: z.string(),
  shotStyleId: z.string().nullable(),
  startSeconds: z.number().nullable(),
  endSeconds: z.number().nullable(),
  arguments: jsonRecord,
  ok: z.boolean(),
  refusal: z.string().nullable(),
});

export const inspectionSchema = z.object({
  unavailableReason: z.string().nullable(),
  catalogId: z.string().nullable(),
  catalogRevision: z.string().nullable(),
  catalogStyles: z.array(
    z.object({
      shotStyleId: z.string(),
      name: z.string().nullable(),
      category: z.string().nullable(),
      unavailableReason: z.string().nullable(),
    })
  ),
  durationMs: z.number().int().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  userPrompt: z.string().nullable(),
  shots: z.array(inspectionShotSchema),
  words: z.array(z.object({ text: z.string(), startMs: z.number(), endMs: z.number() })),
  media: z.array(inspectionMediaSchema),
  planner: z
    .object({
      planner: z.enum(["agent", "jev"]),
      model: z.string().nullable(),
      turns: z.number().int().nullable(),
      toolCalls: z.number().int().nullable(),
      latencySeconds: z.number().nullable(),
      inputTokens: z.number().int().nullable(),
      outputTokens: z.number().int().nullable(),
      steps: z.array(
        z.object({
          turn: z.number().int(),
          latencyMs: z.number().int().nullable(),
          costUsd: z.number().nullable(),
          texts: z.array(z.string()),
          calls: z.array(inspectionToolCallSchema),
        })
      ),
    })
    .nullable(),
});

export const attemptInspectionSchema = inspectionSchema.extend({ attemptId: z.string() });

export type Inspection = z.infer<typeof inspectionSchema>;
export type AttemptInspection = z.infer<typeof attemptInspectionSchema>;
export type InspectionShot = z.infer<typeof inspectionShotSchema>;
export type InspectionMedia = z.infer<typeof inspectionMediaSchema>;
export type InspectionToolCall = z.infer<typeof inspectionToolCallSchema>;
